/* eslint-disable @typescript-eslint/ban-ts-comment */
import Input from "components/EvtKit/input/Input"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"

import { ChangeElement, ListElement } from "hooks/useHandleObjList"
import { PricingTypes, selectors } from "pages/purchase-plans/constants/defaultValues"

import usePricingVariables from "pages/purchase-plans/hooks/usePricingVariables"
import { mapPricingVariableCalculator } from "pages/purchase-plans/mappers/mapPricingVariables"
import { memo, useCallback, useEffect } from "react"
import styled from "styled-components"
import useDeepCompareEffect from "use-deep-compare-effect"
import { useDeepMemo } from "utils/useDeepMemo"
import EventTypeSelector from "./selectors/EventTypeSelector"
import PricingVariables from "./PricingVariables"

const PricingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 32px;
`

const InlineGridContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  gap: 16px;
`

const InlineFlexContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ItemContainer = styled.div`
  display: flex;
`

const InputContainer = styled.div`
  width: 100%;
  height: 48px;
`

const TextLabel = styled.label`
  font-size: 14px;
  line-height: 18px;
  color: ${(props) => props.theme.palette.primary_one};
`

const SwitchContainer = styled.div`
  display: flex;

  &.calculatedPrice {
    width: 164px;
  }
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.disabled};
`

interface PricingElementProps {
  item: ListElement<Partial<App.Pricings>>
  changeElement: ChangeElement<App.Pricings>
}

const PricingCalculatorElement = (props: PricingElementProps) => {
  const { item, changeElement } = props

  const { list, mathExp } = useDeepMemo(() => {
    return {
      list: item.pricingCalculator?.scope.map(mapPricingVariableCalculator) ?? [],
      mathExp: item.pricingCalculator?.strExpression
    }
  }, [item.pricingCalculator])

  const { props: calculatorVariableProps } = usePricingVariables(PricingTypes.VARIABLE_CALCULATOR, {
    list,
    mathExp
  })

  useEffect(() => {
    if (!item.pricingCalculator) return

    item.pricingCalculator.strExpression = calculatorVariableProps.mathExpression
  }, [calculatorVariableProps.mathExpression])

  useDeepCompareEffect(() => {
    if (!item.pricingCalculator) return

    item.pricingCalculator.scope = calculatorVariableProps.list?.map(mapPricingVariableCalculator)
  }, [calculatorVariableProps.list])

  const onSelectorChange = useCallback(
    (key: string, name: "source" | "scopeType" | "parameter") => (e: Option | null) => {
      const optionValue = e?.value ?? ""

      changeElement(key, name, optionValue)
    },
    []
  )

  return (
    <PricingContainer key={item.key}>
      <InlineGridContainer>
        <InputContainer>
          <TextLabel></TextLabel>
          <Input
            type="text"
            name="variableNameInExpression"
            label="Variável"
            className="fullWidth"
            value={item.variableNameInExpression}
            readOnly
            shrink
          />
        </InputContainer>

        <ItemContainer>
          <TextLabel></TextLabel>
          <InputContainer>
            <Input
              type="text"
              name="variableLabelForDisplay"
              label="Nome de exibição *"
              className="fullWidth"
              value={item.variableLabelForDisplay}
              onChange={(e) => {
                changeElement(item.key, "variableLabelForDisplay", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>
        </ItemContainer>

        <ItemContainer>
          <EventTypeSelector onSelectorChange={onSelectorChange(item.key, "source")} value={item.source} />
        </ItemContainer>

        <ItemContainer>
          <SelectV2
            className="fullWidth"
            id="scopeType"
            label="Tipo de escopo *"
            options={selectors.scopeTypeOptionsCalculator}
            placeholder="Selecione"
            shrink
            value={item.scopeType || ""}
            onChange={onSelectorChange(item.key, "scopeType")}
          />
        </ItemContainer>
      </InlineGridContainer>

      <InlineGridContainer>
        <SwitchContainer>
          <SwitchV2
            label="Exibição visivel?"
            value={!!item.isVisible}
            onChange={() => changeElement(item.key, "isVisible", !item.isVisible)}
            variant="medium"
            grayBackground
          />
        </SwitchContainer>
        <ItemContainer>
          <TextLabel></TextLabel>
          <InputContainer>
            <Input
              type="text"
              name="baseUnit"
              label="Unidade base *"
              className="fullWidth"
              value={item.baseUnit}
              onChange={(e) => {
                changeElement(item.key, "baseUnit", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>
        </ItemContainer>

        <ItemContainer>
          <TextLabel></TextLabel>
          <InputContainer>
            <Input
              type="number"
              name="min"
              label="Valor mínimo"
              className="fullWidth"
              value={item.min}
              onChange={(e) => {
                changeElement(item.key, "min", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>
        </ItemContainer>

        <ItemContainer>
          <TextLabel></TextLabel>
          <InputContainer>
            <Input
              type="number"
              name="max"
              label="Valor máximo"
              className="fullWidth"
              value={item.max}
              onChange={(e) => {
                changeElement(item.key, "max", e.target.value)
              }}
              shrink
              placeholder="Digite"
            />
          </InputContainer>
        </ItemContainer>
      </InlineGridContainer>

      <InlineFlexContainer>
        {item.scopeType === "event-type" && (
          <ItemContainer>
            <SelectV2
              className="fullWidth"
              id="parameter"
              label="Parâmetro *"
              options={selectors.parametersOptionsCalculator}
              placeholder="Selecione"
              shrink
              value={item.parameter || ""}
              onChange={onSelectorChange(item.key, "parameter")}
            />
          </ItemContainer>
        )}

        <SwitchContainer className="calculatedPrice">
          <SwitchV2
            label="É um preço calculado?"
            value={!!item.isCalculated}
            onChange={() => changeElement(item.key, "isCalculated", !item.isCalculated)}
            variant="medium"
            grayBackground
          />
        </SwitchContainer>

        {item.isCalculated && (
          <PricingVariables
            {...calculatorVariableProps}
            label="Expressão cálculo de preço da variável"
            pricingType={PricingTypes.VARIABLE_CALCULATOR}
          />
        )}
      </InlineFlexContainer>

      <Divider />
    </PricingContainer>
  )
}

export default memo(PricingCalculatorElement)
